import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFlightsStore, Flight, FlightStatus } from '../store/flights';
import { useFlightAttributesStore } from '../store/flightAttributes';
import { useProjectsStore } from '../store/projects';
import { usePostsStore, Post } from '../store/posts';
import { useAuthStore, FlightsPreferences } from '../store/auth';
import { EditFlightModal } from '../components/EditFlightModal';
import { PostDetailModal } from '../components/PostDetailModal';
import { PageHeader } from '../components/PageHeader';
import { ReportGeneratorModal } from '../components/ReportGeneratorModal';
import { PostType } from '../config/formatSpecs';
import { FLIGHT_STATUS, POST_STATUS } from '../config/statusConfig';
import { StatsCard, StatsCardGrid } from '../components/StatsCard';
import { AttributeManager } from '../components/AttributeManager';

// Inline action dropdown component
function FlightActionsDropdown({
  flight,
  onEdit,
  onStatusChange,
  onDuplicate,
  onDelete,
}: {
  flight: Flight;
  onEdit: () => void;
  onStatusChange: (status: FlightStatus) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation('flights');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getStatusActions = () => {
    const actions: { status: FlightStatus; label: string; icon: string }[] = [];
    switch (flight.status) {
      case 'draft':
        actions.push({ status: 'active', label: t('activate'), icon: '▶️' });
        break;
      case 'active':
        actions.push({ status: 'paused', label: t('pause'), icon: '⏸️' });
        actions.push({ status: 'completed', label: t('complete'), icon: '✅' });
        break;
      case 'paused':
        actions.push({ status: 'active', label: t('resumeFlight'), icon: '▶️' });
        actions.push({ status: 'completed', label: t('complete'), icon: '✅' });
        break;
      case 'completed':
        actions.push({ status: 'archived', label: t('archive'), icon: '📦' });
        actions.push({ status: 'active', label: t('reactivate'), icon: '🔄' });
        break;
      case 'archived':
        actions.push({ status: 'active', label: t('reactivate'), icon: '🔄' });
        break;
    }
    return actions;
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        title={t('actions')}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
          {/* Edit */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('edit')}
          </button>

          {/* Status actions */}
          {getStatusActions().map(({ status, label, icon }) => (
            <button
              key={status}
              onClick={(e) => {
                e.stopPropagation();
                onStatusChange(status);
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <span className="w-4 text-center">{icon}</span>
              {label}
            </button>
          ))}

          <div className="border-t my-1" />

          {/* Duplicate */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            {t('duplicate')}
          </button>

          <div className="border-t my-1" />

          {/* Delete */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            {t('delete')}
          </button>
        </div>
      )}
    </div>
  );
}

type GroupBy = 'none' | 'status' | 'date';
type SortBy = 'name' | 'start_date' | 'end_date' | 'created_at';
type SortOrder = 'asc' | 'desc';

export function FlightsPage() {
  const { t } = useTranslation('flights');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projects, fetchProjects, selectedProjectId, setSelectedProjectId } = useProjectsStore();
  const { flights, fetchFlights, clearFlights, updateFlight, createFlight, deleteFlight } = useFlightsStore();
  const { attributes, fetchAttributes } = useFlightAttributesStore();
  const { posts, fetchPosts, updatePost, deletePost, cancelPost, duplicatePost } = usePostsStore();
  const { user, updatePreferences } = useAuthStore();

  // Get saved preferences
  const savedPrefs = user?.preferences?.flights;

  const [statusFilter, setStatusFilter] = useState<FlightStatus | 'all'>(savedPrefs?.statusFilter || 'all');
  const [attributeFilter, setAttributeFilter] = useState<string | 'all'>('all');
  const [groupBy, setGroupBy] = useState<GroupBy>(savedPrefs?.groupBy || 'none');
  const [sortBy, setSortBy] = useState<SortBy>(savedPrefs?.sortBy || 'start_date');
  const [sortOrder, setSortOrder] = useState<SortOrder>(savedPrefs?.sortOrder || 'desc');
  const [isLoading, setIsLoading] = useState(true);
  const [editingFlight, setEditingFlight] = useState<Flight | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAttributeManager, setShowAttributeManager] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Post Detail Modal state
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  // Save preferences to DB
  const savePreferences = useCallback(async (prefs: Partial<FlightsPreferences>) => {
    const currentPrefs = user?.preferences?.flights || {};
    const newPrefs = { ...currentPrefs, ...prefs };
    await updatePreferences({ flights: newPrefs as FlightsPreferences });
  }, [user?.preferences?.flights, updatePreferences]);

  // Load projects
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Set default project once loaded
  useEffect(() => {
    if (projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId, setSelectedProjectId]);

  // Load flights and attributes when project changes
  useEffect(() => {
    if (selectedProjectId) {
      setIsLoading(true);
      Promise.all([
        fetchFlights(selectedProjectId),
        fetchPosts(selectedProjectId),
        fetchAttributes(selectedProjectId),
      ]).finally(() => setIsLoading(false));
    } else {
      clearFlights();
    }
  }, [selectedProjectId, fetchFlights, fetchPosts, fetchAttributes, clearFlights]);

  // Handle URL params for post modal
  useEffect(() => {
    const postId = searchParams.get('post');
    if (postId && posts.length > 0) {
      const post = posts.find(p => p.id === postId);
      if (post) {
        setSelectedPost(post);
        setIsPostModalOpen(true);
      }
    }
  }, [searchParams, posts]);

  // Open post detail modal
  const handlePostClick = (post: Post, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedPost(post);
    setIsPostModalOpen(true);
    setSearchParams({ post: post.id });
  };

  // Close post detail modal
  const handleClosePostModal = () => {
    setSelectedPost(null);
    setIsPostModalOpen(false);
    searchParams.delete('post');
    setSearchParams(searchParams);
  };

  // Handle post update from modal
  const handlePostUpdate = async (postId: string, data: { text?: string; scheduled_for?: string; platforms?: string[]; status?: 'draft' | 'scheduled'; media_urls?: string[]; post_type?: PostType; flight_id?: string | null; hashtags?: string[] }) => {
    await updatePost(postId, data);
    // Refresh posts to get updated data
    if (selectedProjectId) {
      await fetchPosts(selectedProjectId);
    }
  };

  // Handle post delete from modal
  const handlePostDelete = async (postId: string, _options?: { deleteFromPlatforms?: boolean }) => {
    const result = await deletePost(postId);
    handleClosePostModal();
    // Refresh posts
    if (selectedProjectId) {
      await fetchPosts(selectedProjectId);
    }
    return result;
  };

  // Handle post cancel from modal
  const handlePostCancel = async (postId: string) => {
    await cancelPost(postId);
    // Refresh posts
    if (selectedProjectId) {
      await fetchPosts(selectedProjectId);
    }
  };

  // Handle status filter change with persistence
  const handleStatusFilterChange = (newStatus: FlightStatus | 'all') => {
    setStatusFilter(newStatus);
    savePreferences({ statusFilter: newStatus });
  };

  // Handle groupBy change with persistence
  const handleGroupByChange = (newGroupBy: GroupBy) => {
    setGroupBy(newGroupBy);
    savePreferences({ groupBy: newGroupBy });
  };

  // Handle sortBy change with persistence
  const handleSortByChange = (newSortBy: SortBy) => {
    setSortBy(newSortBy);
    savePreferences({ sortBy: newSortBy });
  };

  // Handle sortOrder change with persistence
  const handleSortOrderChange = (newSortOrder: SortOrder) => {
    setSortOrder(newSortOrder);
    savePreferences({ sortOrder: newSortOrder });
  };

  // Group posts per flight with counts and sorted posts list
  const postsByFlight = useMemo(() => {
    const grouped: Record<string, {
      total: number;
      scheduled: number;
      published: number;
      draft: number;
      posts: Post[];
    }> = {};
    posts.forEach((post) => {
      if (post.flight_id) {
        if (!grouped[post.flight_id]) {
          grouped[post.flight_id] = { total: 0, scheduled: 0, published: 0, draft: 0, posts: [] };
        }
        grouped[post.flight_id].total++;
        grouped[post.flight_id].posts.push(post);
        if (post.status === 'scheduled') grouped[post.flight_id].scheduled++;
        if (post.status === 'published') grouped[post.flight_id].published++;
        if (post.status === 'draft') grouped[post.flight_id].draft++;
      }
    });
    // Sort posts by scheduled_for date
    Object.values(grouped).forEach(group => {
      group.posts.sort((a, b) => new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime());
    });
    return grouped;
  }, [posts]);

  // Helper function to get thumbnail URL
  const getThumbnailUrl = (post: Post): string | null => {
    if (post.media_urls && post.media_urls.length > 0) {
      return post.media_urls[0];
    }
    return null;
  };

  // Filter, sort and group flights
  const filteredFlights = useMemo(() => {
    // First filter by status
    let result = statusFilter === 'all' ? [...flights] : flights.filter((f) => f.status === statusFilter);

    // Then filter by attribute
    if (attributeFilter !== 'all') {
      result = result.filter((f) =>
        f.attributes?.some((attr) => attr.id === attributeFilter)
      );
    }

    // Then sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'start_date':
          comparison = new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
          break;
        case 'end_date':
          comparison = new Date(a.end_date).getTime() - new Date(b.end_date).getTime();
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [flights, statusFilter, attributeFilter, sortBy, sortOrder]);

  // Group flights for display
  const groupedFlights = useMemo(() => {
    if (groupBy === 'none') {
      return [{ title: null, flights: filteredFlights }];
    }

    const groups: Record<string, Flight[]> = {};

    filteredFlights.forEach((flight) => {
      let groupKey: string;

      if (groupBy === 'status') {
        groupKey = flight.status;
      } else if (groupBy === 'date') {
        // Group by month
        const date = new Date(flight.start_date);
        groupKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      } else {
        groupKey = 'other';
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(flight);
    });

    // Convert to array and sort groups
    const groupOrder = groupBy === 'status'
      ? ['active', 'paused', 'draft', 'completed', 'archived']
      : Object.keys(groups).sort((a, b) => b.localeCompare(a)); // Date descending

    return groupOrder
      .filter(key => groups[key]?.length > 0)
      .map(key => ({
        title: groupBy === 'status'
          ? t(`status.${key}`, { defaultValue: key })
          : formatGroupDate(key),
        flights: groups[key],
      }));
  }, [filteredFlights, groupBy, t]);

  // Helper to format group date
  const formatGroupDate = (dateKey: string) => {
    const [year, month] = dateKey.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  };

  // Group flights by status for counts
  const flightsByStatus = useMemo(() => {
    const grouped: Record<FlightStatus, Flight[]> = {
      draft: [],
      active: [],
      paused: [],
      completed: [],
      archived: [],
    };
    flights.forEach((f) => {
      grouped[f.status].push(f);
    });
    return grouped;
  }, [flights]);

  const formatDate = (date: string) => {
    const [year, month, day] = date.split('T')[0].split('-').map(Number);
    const localDate = new Date(year, month - 1, day);
    return localDate.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const calculateProgress = (flight: Flight) => {
    const [startYear, startMonth, startDay] = flight.start_date.split('T')[0].split('-').map(Number);
    const [endYear, endMonth, endDay] = flight.end_date.split('T')[0].split('-').map(Number);
    const start = new Date(startYear, startMonth - 1, startDay).getTime();
    const end = new Date(endYear, endMonth - 1, endDay).getTime();
    const now = Date.now();
    const progress = ((now - start) / (end - start)) * 100;
    return Math.min(100, Math.max(0, progress));
  };

  const handleStatusChange = async (flightId: string, newStatus: FlightStatus) => {
    setActionLoading(flightId);
    try {
      await updateFlight(flightId, { status: newStatus });
    } catch (err) {
      console.error('Status update failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDuplicate = async (flight: Flight) => {
    if (!selectedProjectId) return;
    setActionLoading(flight.id);
    try {
      // Create a copy with "(Kopie)" suffix
      const today = new Date().toISOString().split('T')[0];
      const thirtyDaysLater = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      await createFlight({
        project_id: selectedProjectId,
        name: `${flight.name} (Kopie)`,
        description: flight.description || undefined,
        start_date: today,
        end_date: thirtyDaysLater,
        ai_context: flight.ai_context || undefined,
        goals: flight.goals || undefined,
        color: flight.color || undefined,
      });
    } catch (err) {
      console.error('Duplicate failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (flightId: string) => {
    if (!confirm(t('confirmDelete'))) return;
    setActionLoading(flightId);
    try {
      await deleteFlight(flightId);
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        icon={
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        }
        title={t('title')}
        subtitle={t('subtitle')}
        breadcrumbs={selectedProject ? [
          { label: t('common:projects', 'Projekte'), href: '/projects' },
          { label: selectedProject.name, href: `/projects/${selectedProject.id}` },
        ] : [{ label: 'Dashboard', href: '/' }]}
        actions={
          <div className="flex items-center gap-3">
            {/* Project Selector */}
            {projects.length > 0 && (
              <select
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
              >
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            )}

            {/* Report Button */}
            {selectedProjectId && (
              <button
                onClick={() => setIsReportModalOpen(true)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                title={t('report')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">{t('report')}</span>
              </button>
            )}

            {/* Attribute Manager Button */}
            {selectedProjectId && (
              <button
                onClick={() => setShowAttributeManager(true)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                title={t('attributes')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                {t('attributes')}
              </button>
            )}

            {/* Create button */}
            {selectedProjectId && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('newFlight')}
              </button>
            )}
          </div>
        }
      />

      {/* Stats Overview */}
      <div className="mb-8">
        <StatsCardGrid columns={5}>
          <StatsCard
            value={flightsByStatus.active.length}
            label={t('status.active')}
            config={FLIGHT_STATUS.active}
            isActive={statusFilter === 'active'}
            onClick={() => handleStatusFilterChange(statusFilter === 'active' ? 'all' : 'active')}
          />
          <StatsCard
            value={posts.filter(p => p.status === 'scheduled' && p.flight_id).length}
            label={t('scheduled')}
            config={POST_STATUS.scheduled}
          />
          <StatsCard
            value={flightsByStatus.draft.length}
            label={t('status.draft')}
            config={FLIGHT_STATUS.draft}
            isActive={statusFilter === 'draft'}
            onClick={() => handleStatusFilterChange(statusFilter === 'draft' ? 'all' : 'draft')}
          />
          <StatsCard
            value={flightsByStatus.completed.length}
            label={t('status.completed')}
            config={FLIGHT_STATUS.completed}
            isActive={statusFilter === 'completed'}
            onClick={() => handleStatusFilterChange(statusFilter === 'completed' ? 'all' : 'completed')}
          />
          <StatsCard
            value={flightsByStatus.archived.length}
            label={t('status.archived')}
            config={FLIGHT_STATUS.archived}
            isActive={statusFilter === 'archived'}
            onClick={() => handleStatusFilterChange(statusFilter === 'archived' ? 'all' : 'archived')}
          />
        </StatsCardGrid>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        {/* Status filter pills */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{t('filters.status')}:</span>
          <button
            onClick={() => handleStatusFilterChange('all')}
            className={`px-3 py-1 text-sm rounded-full transition-colors ${
              statusFilter === 'all'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t('filters.all')} ({flights.length})
          </button>
          {(['active', 'draft', 'paused', 'completed'] as FlightStatus[]).map((status) => {
            const config = FLIGHT_STATUS[status];
            return flightsByStatus[status].length > 0 && (
              <button
                key={status}
                onClick={() => handleStatusFilterChange(statusFilter === status ? 'all' : status)}
                className={`px-3 py-1 text-sm rounded-full transition-colors flex items-center gap-1.5 ${
                  statusFilter === status
                    ? `${config.bg} ${config.text} ring-1 ${config.ring}`
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${config.dot}`} />
                {t(`status.${status}`)} ({flightsByStatus[status].length})
              </button>
            );
          })}
        </div>

        {/* Divider */}
        <div className="h-6 w-px bg-gray-200" />

        {/* Attribute Filter */}
        {attributes.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{t('filters.attribute')}:</span>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setAttributeFilter('all')}
                className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                  attributeFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('filters.all')}
              </button>
              {attributes.map((attr) => {
                const count = flights.filter(f => f.attributes?.some(a => a.id === attr.id)).length;
                if (count === 0) return null;
                return (
                  <button
                    key={attr.id}
                    onClick={() => setAttributeFilter(attributeFilter === attr.id ? 'all' : attr.id)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full transition-colors ${
                      attributeFilter === attr.id
                        ? 'ring-1 ring-offset-1'
                        : 'hover:ring-1 hover:ring-offset-1'
                    }`}
                    style={{
                      backgroundColor: attributeFilter === attr.id ? attr.color : `${attr.color}20`,
                      color: attributeFilter === attr.id ? 'white' : attr.color,
                      ['--tw-ring-color' as string]: attr.color,
                    }}
                  >
                    {attr.is_system && (
                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    )}
                    {attr.name} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="h-6 w-px bg-gray-200" />

        {/* Group By */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{t('filters.groupBy')}:</span>
          <select
            value={groupBy}
            onChange={(e) => handleGroupByChange(e.target.value as GroupBy)}
            className="px-3 py-1 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="none">{t('filters.groupByNone')}</option>
            <option value="status">{t('filters.groupByStatus')}</option>
            <option value="date">{t('filters.groupByDate')}</option>
          </select>
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{t('filters.sortBy')}:</span>
          <select
            value={sortBy}
            onChange={(e) => handleSortByChange(e.target.value as SortBy)}
            className="px-3 py-1 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="start_date">{t('filters.sortByStartDate')}</option>
            <option value="end_date">{t('filters.sortByEndDate')}</option>
            <option value="name">{t('filters.sortByName')}</option>
            <option value="created_at">{t('filters.sortByCreatedAt')}</option>
          </select>
          <button
            onClick={() => handleSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title={sortOrder === 'asc' ? t('filters.ascending') : t('filters.descending')}
          >
            {sortOrder === 'asc' ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Flights List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
              <div className="h-6 w-48 bg-gray-200 rounded mb-3"></div>
              <div className="h-4 w-32 bg-gray-100 rounded"></div>
            </div>
          ))}
        </div>
      ) : filteredFlights.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <svg className="w-20 h-20 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('empty.titleFiltered')}</h3>
          <p className="text-gray-500 mb-6">
            {statusFilter !== 'all'
              ? t('empty.descriptionFiltered')
              : t('empty.description')}
          </p>
          {selectedProjectId && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('createFlight')}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {groupedFlights.map((group) => (
            <div key={group.title || 'all'}>
              {/* Group Header */}
              {group.title && (
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 px-1">
                  {group.title} ({group.flights.length})
                </h3>
              )}

              {/* Flights in Group */}
              <div className="space-y-4">
                {group.flights.map((flight) => {
                  const flightData = postsByFlight[flight.id] || { total: 0, scheduled: 0, published: 0, draft: 0, posts: [] };
                  const progress = calculateProgress(flight);
                  const isActionLoading = actionLoading === flight.id;
                  const postsWithThumbnails = flightData.posts.filter(p => p.media_urls && p.media_urls.length > 0).slice(0, 8);

                  const flightStatusConfig = FLIGHT_STATUS[flight.status];
                  return (
                    <div
                      key={flight.id}
                      onClick={() => navigate(`/flights/${flight.id}`)}
                      className={`rounded-xl border border-gray-200 border-l-[6px] ${flightStatusConfig.accent} p-6 hover:border-gray-300 hover:shadow-md cursor-pointer transition-all group ${flightStatusConfig.cardBg} ${
                        isActionLoading ? 'opacity-50 pointer-events-none' : ''
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        {/* Color indicator */}
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: flight.color || '#3B82F6' }}
                        >
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-lg font-semibold text-gray-900 truncate">{flight.name}</h3>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${flightStatusConfig.bg} ${flightStatusConfig.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${flightStatusConfig.dot}`} />
                              {t(`status.${flight.status}`)}
                            </span>
                            {/* Attribute Badges */}
                            {flight.attributes && flight.attributes.length > 0 && (
                              <div className="flex items-center gap-1.5">
                                {flight.attributes.map((attr) => (
                                  <span
                                    key={attr.id}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full"
                                    style={{
                                      backgroundColor: `${attr.color}20`,
                                      color: attr.color,
                                    }}
                                    title={attr.is_system ? `${attr.name} (System)` : attr.name}
                                  >
                                    {attr.is_system && (
                                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                      </svg>
                                    )}
                                    {attr.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          {flight.description && (
                            <p className="text-gray-600 text-sm mb-3 line-clamp-1">{flight.description}</p>
                          )}

                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {formatDate(flight.start_date)} - {formatDate(flight.end_date)}
                            </span>

                            <span className="text-gray-300">|</span>

                            <span className="flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              {t('postsCount', { count: flightData.total })}
                              {flightData.scheduled > 0 && (
                                <span className="text-blue-600">({t('scheduledCount', { count: flightData.scheduled })})</span>
                              )}
                            </span>

                            <span className="font-mono text-xs text-gray-400">{flight.display_id}</span>
                          </div>

                          {/* Goal Progress & Next Post Section */}
                          {(() => {
                            const goalPostCount = flight.goals?.post_count;
                            const hasGoal = goalPostCount && goalPostCount > 0;
                            const goalProgress = hasGoal ? Math.min(100, (flightData.total / goalPostCount) * 100) : 0;
                            const nextScheduledPost = flightData.posts
                              .filter(p => p.status === 'scheduled' && new Date(p.scheduled_for) > new Date())
                              .sort((a, b) => new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime())[0];
                            const isActive = flight.status === 'active';
                            const hasNoScheduledPosts = isActive && flightData.scheduled === 0;
                            const isBehindSchedule = hasGoal && isActive && flightData.total < (goalPostCount * (progress / 100));

                            return (
                              <div className="mt-3 space-y-2">
                                {/* Warning Indicators */}
                                {(hasNoScheduledPosts || isBehindSchedule) && (
                                  <div className="flex flex-wrap gap-2">
                                    {hasNoScheduledPosts && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-medium rounded-full">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        {t('noScheduledPosts')}
                                      </span>
                                    )}
                                    {isBehindSchedule && !hasNoScheduledPosts && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 text-orange-700 text-xs font-medium rounded-full">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {t('behindSchedule')}
                                      </span>
                                    )}
                                  </div>
                                )}

                                {/* Goal Progress Bar */}
                                {hasGoal && (
                                  <div>
                                    <div className="flex items-center justify-between text-xs mb-1">
                                      <span className="text-gray-500 flex items-center gap-1">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                        {t('postGoal')}
                                      </span>
                                      <span className={`font-medium ${flightData.total >= goalPostCount ? 'text-green-600' : 'text-gray-700'}`}>
                                        {flightData.total} / {goalPostCount}
                                        {flightData.total >= goalPostCount && (
                                          <svg className="w-3 h-3 inline ml-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                      </span>
                                    </div>
                                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                      <div
                                        className={`h-full rounded-full transition-all ${flightData.total >= goalPostCount ? 'bg-green-500' : ''}`}
                                        style={{
                                          width: `${goalProgress}%`,
                                          backgroundColor: flightData.total >= goalPostCount ? undefined : (flight.color || '#3B82F6'),
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Next Scheduled Post */}
                                {nextScheduledPost && (
                                  <button
                                    onClick={(e) => handlePostClick(nextScheduledPost, e)}
                                    className="flex items-center gap-2 w-full text-left p-2 -mx-2 rounded-lg hover:bg-gray-50 transition-colors group/next"
                                  >
                                    <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0 bg-gray-100">
                                      {nextScheduledPost.media_urls?.[0] ? (
                                        <img
                                          src={nextScheduledPost.media_urls[0]}
                                          alt=""
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                          </svg>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs text-gray-500">{t('nextPost')}</div>
                                      <div className="text-sm font-medium text-gray-900 truncate">
                                        {new Date(nextScheduledPost.scheduled_for).toLocaleDateString('de-DE', {
                                          weekday: 'short',
                                          day: 'numeric',
                                          month: 'short',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </div>
                                    </div>
                                    <svg className="w-4 h-4 text-gray-300 group-hover/next:text-gray-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            );
                          })()}

                          {/* Post Thumbnails */}
                          {postsWithThumbnails.length > 0 && (
                            <div className="flex items-center gap-1.5 mt-3 overflow-hidden">
                              {postsWithThumbnails.map((post) => {
                                const thumbnail = getThumbnailUrl(post);
                                return (
                                  <button
                                    key={post.id}
                                    onClick={(e) => handlePostClick(post, e)}
                                    className="relative w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200 bg-gray-100 hover:ring-2 hover:ring-blue-500 hover:ring-offset-1 transition-all"
                                    title={`${post.text?.slice(0, 50) || 'Post'}... - ${new Date(post.scheduled_for).toLocaleDateString('de-DE')}`}
                                  >
                                    {thumbnail ? (
                                      <img
                                        src={thumbnail}
                                        alt=""
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                      </div>
                                    )}
                                    {/* Status indicator dot */}
                                    <div className={`absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full border border-white ${POST_STATUS[post.status]?.dot || 'bg-gray-300'}`} />
                                  </button>
                                );
                              })}
                              {flightData.total > postsWithThumbnails.length && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/posts?flight=${flight.id}`);
                                  }}
                                  className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0 hover:bg-gray-200 transition-colors"
                                  title={t('showAllPosts')}
                                >
                                  <span className="text-xs text-gray-500 font-medium">
                                    +{flightData.total - postsWithThumbnails.length}
                                  </span>
                                </button>
                              )}
                            </div>
                          )}

                          {/* Time Progress bar (only for active flights without goal) */}
                          {flight.status === 'active' && !flight.goals?.post_count && (
                            <div className="mt-4">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-gray-500">{t('progress')}</span>
                                <span className="font-medium text-gray-700">{Math.round(progress)}%</span>
                              </div>
                              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{
                                    width: `${progress}%`,
                                    backgroundColor: flight.color || '#3B82F6',
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <FlightActionsDropdown
                            flight={flight}
                            onEdit={() => setEditingFlight(flight)}
                            onStatusChange={(status) => handleStatusChange(flight.id, status)}
                            onDuplicate={() => handleDuplicate(flight)}
                            onDelete={() => handleDelete(flight.id)}
                          />
                          {/* Arrow */}
                          <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && selectedProjectId && (
        <EditFlightModal
          projectId={selectedProjectId}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Edit Modal */}
      {editingFlight && (
        <EditFlightModal
          flight={editingFlight}
          onClose={() => setEditingFlight(null)}
        />
      )}

      {/* Post Detail Modal */}
      {isPostModalOpen && selectedPost && (
        <PostDetailModal
          post={selectedPost}
          isOpen={isPostModalOpen}
          onClose={handleClosePostModal}
          onUpdate={handlePostUpdate}
          onDelete={handlePostDelete}
          onCancel={handlePostCancel}
          onDuplicate={async (postId) => {
            await duplicatePost(postId);
            handleClosePostModal();
            if (selectedProjectId) {
              fetchPosts(selectedProjectId, { limit: 500 });
            }
          }}
          flights={flights.map(f => ({ id: f.id, name: f.name, color: f.color || '#3B82F6' }))}
        />
      )}

      {/* Attribute Manager Modal */}
      {selectedProjectId && (
        <AttributeManager
          projectId={selectedProjectId}
          isOpen={showAttributeManager}
          onClose={() => {
            setShowAttributeManager(false);
            // Refresh flights to get updated attribute assignments
            fetchFlights(selectedProjectId);
          }}
        />
      )}

      {/* Report Generator Modal */}
      {selectedProjectId && (
        <ReportGeneratorModal
          projectId={selectedProjectId}
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
        />
      )}
    </div>
  );
}

export default FlightsPage;
